import React, {
  createContext, useContext, useMemo, useReducer,
} from 'react';

import type { Session } from '@supabase/supabase-js';

export type ActivationStep =
  | 'access_code_entry'
  | 'auth_method_selection'
  | 'phone_entry'
  | 'otp_verification'
  | 'email_entry'
  | 'magic_link_sent'
  | 'email_password'
  | 'coverage_form'
  | 'activation_complete'
  | 'error';

export type ActivationPath = 'employer' | 'direct';
export type Platform = 'web' | 'native';
export type AuthMethod = 'phone' | 'email' | 'email_password';
export enum AccountIntent {
  Create = 'create',
  Signin = 'signin',
}

export interface ActivationState {
  step: ActivationStep;
  path: ActivationPath | null;
  platform: Platform;
  accountIntent: AccountIntent;
  authMethod: AuthMethod | null;
  orgSlug: string;
  // Access code for employer/access-code flows (future — see access_code_entry step).
  // null when org_slug is provided directly (Sleepio beta path).
  accessCode: string | null;
  personId: string | null;
  phone: string | null;
  email: string | null;
  session: Session | null;
  error: string | null;
  /** Native deep-link URI passed by Canopy when opening CAP (e.g. sleepio://exchange-callback).
   *  Threaded through navigation URLs so ActivationComplete can build the handoff redirect. */
  redirectTo: string | null;
}

export type ActivationAction =
  | { type: 'SET_STEP'; step: ActivationStep }
  | { type: 'SET_AUTH_METHOD'; authMethod: AuthMethod }
  | { type: 'SET_PLATFORM'; platform: Platform }
  | { type: 'SET_PATH'; path: ActivationPath }
  | { type: 'SET_ACCOUNT_INTENT'; accountIntent: AccountIntent }
  | { type: 'SET_PHONE'; phone: string }
  | { type: 'SET_EMAIL'; email: string }
  | { type: 'SET_SESSION'; session: Session }
  | { type: 'SET_ORG_SLUG'; orgSlug: string }
  | { type: 'SET_ACCESS_CODE'; accessCode: string }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_REDIRECT_TO'; redirectTo: string };

const baseInitialState: Omit<ActivationState, 'orgSlug' | 'accountIntent'> = {
  step: 'auth_method_selection',
  path: null,
  platform: 'web',
  authMethod: null,
  accessCode: null,
  personId: null,
  phone: null,
  email: null,
  session: null,
  error: null,
  redirectTo: null,
};

function reducer(state: ActivationState, action: ActivationAction): ActivationState {
  switch (action.type) {
  case 'SET_STEP':
    return { ...state, step: action.step };
  case 'SET_AUTH_METHOD':
    return { ...state, authMethod: action.authMethod };
  case 'SET_PLATFORM':
    return { ...state, platform: action.platform };
  case 'SET_PATH':
    return { ...state, path: action.path };
  case 'SET_ACCOUNT_INTENT':
    return { ...state, accountIntent: action.accountIntent };
  case 'SET_PHONE':
    return { ...state, phone: action.phone };
  case 'SET_EMAIL':
    return { ...state, email: action.email };
  case 'SET_SESSION':
    return { ...state, session: action.session };
  case 'SET_ORG_SLUG':
    return { ...state, orgSlug: action.orgSlug };
  case 'SET_ACCESS_CODE':
    return { ...state, accessCode: action.accessCode };
  case 'SET_ERROR':
    return { ...state, error: action.error };
  case 'SET_REDIRECT_TO':
    return { ...state, redirectTo: action.redirectTo };
  default:
    return state;
  }
}

const ActivationContext = createContext<{
  state: ActivationState;
  dispatch: React.Dispatch<ActivationAction>;
} | null>(null);

export const ActivationProvider: React.FC<{
  children: React.ReactNode;
  initialOrgSlug: string;
  initialAccountIntent: AccountIntent;
}> = ({ children, initialOrgSlug, initialAccountIntent }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...baseInitialState,
    orgSlug: initialOrgSlug,
    accountIntent: initialAccountIntent,
  });
  const value = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <ActivationContext.Provider value={value}>
      {children}
    </ActivationContext.Provider>
  );
};

export function useActivation() {
  const ctx = useContext(ActivationContext);
  if (!ctx) {
    throw new Error('useActivation must be used within ActivationProvider');
  }
  return ctx;
}
